import { fontFamily } from "tailwindcss/defaultTheme.js";
import type { OptionalConfig } from "tailwindcss/types/config";

import baseTailwindConfig from "./theme/base-tailwind.config";

const config: Partial<OptionalConfig> = {
  presets: [baseTailwindConfig],
  theme: {
    extend: {
      colors: {
        "elevation-background": "#000000",
        "elevation-surface-1": "#18181A",
        "elevation-outline-1": "#2D2D2D",
        "elevation-outline-2": "#717276",
        "elevation-outline-3": "#EBEBEB",
        "interactive-primary-on": "#191919",
        "interactive-primary-idle": "#FFFFFF",
        "interactive-primary-hover": "#F8F8F8",
        "interactive-primary-disabled": "#707070",
        "interactive-primary-focus": "#F8F8F8",
        "interactive-secondary-on": "#191919",
        "interactive-secondary-idle": "#2D2D2D",
        "interactive-secondary-hover": "#FFFFFF",
        "interactive-secondary-disabled": "#707070",
        "interactive-secondary-focus": "#FFD001",
        "interactive-outlined-on": "#FFFFFF",
        "interactive-outlined-idle": "#F8F8F8",
        "interactive-outlined-hover": "#EBEBEB",
        "interactive-outlined-disabled": "#AAAAAA",
        "interactive-outlined-focus": "#F8F8F8",
        "interactive-text-hover": "#FFFFFF",
        "interactive-text-disabled": "#C6C6C6",
        "interactive-text-focus": "#F8F8F8",
        "text-default-primary": "#FFFFFF",
        "text-default-secondary": "#F8F8F8",
        "text-inverted-primary": "#141414",
        "text-inverted-secondary": "#717276",
        "info-success": "#26B12F",
        "info-success-outline": "#2AC534",
        "info-error": "#F1005D",
        "info-error-outline": "#E30259",
        "info-error-2": "#FB374B",
        "info-warning": "#FF7803",
        "info-warning-outline": "#FF7803",
        "interactive-text-on-bg-2": "#F8F8F8",
        "interactive-text-on-bg": "#FFFFFF",
        "text-default-tertiary": "#EBEBEB",
        "text-inverted-tertiary": "#EBEBEB",
        "text-default-quarternary": "#AAAAAA",
        "interactive-primary-pressed": "#EBEBEB",
        "interactive-secondary-pressed": "#EBEBEB",
        "interactive-outlined-pressed": "#FFFFFF",
        "interactive-text-pressed": "#FFD001",
        "interactive-outlined-on-toggled": "#000000",
        "interactive-outlined-toggled": "#FFFFFF",
        "interactive-icon-idle": "#C6C6C6",
        "interactive-icon-toggled": "#FFD001",
        "interactive-icon-hover": "#C6C6C6",
        "interactive-icon-pressed": "#EDC000",
        "interactive-icon-disabled": "#707070",
        "interactive-icon-focus": "#EBEBEB",
        "interactive-icon-on": "#000000",
        "elevation-surface-2": "#2D2D2D",
        "interactive-tertiary-on": "#F8F8F8",
        "interactive-tertiary-idle": "#707070",
        "interactive-tertiary-hover": "#C6C6C6",
        "interactive-tertiary-pressed": "#AAAAAA",
        "interactive-tertiary-on-disabled": "#C6C6C6",
        "interactive-tertiary-focus": "#FFD001",
        "interactive-tertiary-toggled": "#EBEBEB",
        "interactive-text-on-inverted": "#191919",
        "interactive-tertiary-on-toggled": "#000000",
        "elevation-surface-3": "#A91F45",
        "gradients-gradient-10": "#000000",
        "gradients-gradient-100": "#141414",
        "gradients-gradient-200": "#191919",
        "gradients-gradient-400": "#2D2D2D",
        "gradients-gradient-500": "#707070",
        "gradients-gradient-600": "#AAAAAA",
        "gradients-gradient-700": "#C6C6C6",
        "gradients-gradient-color-200": "#DFF6E1",
        "gradients-gradient-color-300": "#BDEDC0",
        "gradients-gradient-color-500": "#26B12F",
        "gradients-gradient-color-800": "#1C7C22",
        "elevation-surface-4": "#FFD001",
        "text-default-highlight-1": "#209427",
        "text-default-highlight-2": "#BFAC01",
        "text-default-highlight-3": "#3B82F6",
        "lossdog-primary-300": "#E6BB01",
        "lossdog-danger-300": "#ED2C62",
        "lossdog-danger-500": "#B2214A",
        "lossdog-warning-300": "#E66C03",
        "lossdog-warning-500": "#BF5A02",
        "lossdog-blue-300": "#326EDF",
        "lossdog-blue-400": "#295AC6",
      },
      borderRadius: {
        none: "0rem",
        "2xs": "0.125rem",
        xs: "0.25rem",
        s: "0.375rem",
        sm: "0.5rem",
        ml: "0.75rem",
        xl: "1rem",
        "4xl": "1.5rem",
        full: "624.9375rem",
        "5xl": "1.75rem",
        "6xl": "2rem",
        m: "0.625rem",
        l: "0.875rem",
        "2xl": "1.125rem",
        "3xl": "1.25rem",
        "button-rounding": "0.375rem",
        "input-rounding": "0.375rem",
        "focus-rounding": "0.5rem",
      },
      maxWidth: {
        "0": "0rem",
        "1": "0.25rem",
        "2": "0.5rem",
        "3": "0.75rem",
        "4": "1rem",
        "5": "1.25rem",
        "6": "1.5rem",
        "7": "1.75rem",
        "8": "2rem",
        "9": "2.25rem",
        "10": "2.5rem",
        "11": "2.75rem",
        "12": "3rem",
        "14": "3.5rem",
        "16": "4rem",
        "18": "4.5rem",
        "20": "5rem",
        "24": "6rem",
        "28": "7rem",
        "32": "8rem",
        "36": "9rem",
        "40": "10rem",
        "44": "11rem",
        "48": "12rem",
        "52": "13rem",
        "56": "14rem",
        "60": "15rem",
        "64": "16rem",
        "72": "18rem",
        "80": "20rem",
        "96": "24rem",
        xs: "20rem",
        sm: "24rem",
        md: "28rem",
        lg: "32rem",
        xl: "36rem",
        "2xl": "42rem",
        "3xl": "48rem",
        "4xl": "56rem",
        "5xl": "64rem",
        "6xl": "72rem",
        "7xl": "80rem",
        "2xs": "16rem",
        "3xs": "12rem",
        "3-5xs": "9.75rem",
        "4xs": "8rem",
        "4-5xs": "7rem",
        "5-5xs": "4.75rem",
        "5xs": "6rem",
        px: "0.0625rem",
        "0-5": "0.125rem",
        "1-5": "0.375rem",
        "2-5": "0.625rem",
        "3-5": "0.875rem",
        "height-s": "0.625rem",
        "height-m": "1rem",
        "height-l": "1.25rem",
        "height-xs": "0.125rem",
        "icon-to-label-spacing": "0.25rem",
        "input-side-spacing": "0.75rem",
      },
      spacing: {
        "0": "0rem",
        "1": "0.25rem",
        "2": "0.5rem",
        "3": "0.75rem",
        "4": "1rem",
        "5": "1.25rem",
        "6": "1.5rem",
        "7": "1.75rem",
        "8": "2rem",
        "9": "2.25rem",
        "10": "2.5rem",
        "11": "2.75rem",
        "12": "3rem",
        "14": "3.5rem",
        "16": "4rem",
        "18": "4.5rem",
        "20": "5rem",
        "24": "6rem",
        "28": "7rem",
        "32": "8rem",
        "36": "9rem",
        "40": "10rem",
        "44": "11rem",
        "48": "12rem",
        "52": "13rem",
        "56": "14rem",
        "60": "15rem",
        "64": "16rem",
        "72": "18rem",
        "80": "20rem",
        "96": "24rem",
        px: "0.0625rem",
        "0-5": "0.125rem",
        "1-5": "0.375rem",
        "2-5": "0.625rem",
        "3-5": "0.875rem",
        "height-s": "0.625rem",
        "height-m": "1rem",
        "height-l": "1.25rem",
        "height-xs": "0.125rem",
        "icon-to-label-spacing": "0.25rem",
        "input-side-spacing": "0.75rem",
      },
      boxShadow: {
        "1": "0px 2px 3px 0px rgba(0, 0, 0, 0.20)",
        "2": "0px 0px 1px 0px rgba(0, 0, 0, 0.20)",
        "3": "0px 1.13px 0.75px 0px rgba(0, 0, 0, 0.30)",
        "4": "0px 4px 7px 0px rgba(0, 0, 0, 0.25)",
        "5": "0px 2px 3px 0px rgba(0, 0, 0, 0.10)",
        "6": "0px 1px 34px 0px rgba(0, 0, 0, 0.12)",
        inner: "0px 0px 5px 2px rgba(0, 0, 0, 0.07) inset",
      },
      blur: {
        "0": "0px",
        "1": "2px",
        "2": "4px",
        "3": "6px",
        "4": "8px",
        "5": "12px",
        "6": "20px",
        "7": "32px",
      },
      backdropBlur: {
        "1": "2px",
        "2": "4px",
        "3": "6px",
        "4": "8px",
        "5": "12px",
        "6": "20px",
        "7": "32px",
      },
      fontSize: {
        "desktop-headline-1": [
          "2.25rem",
          {
            lineHeight: "1.3333",
            letterSpacing: "0em",
          },
        ],
        "desktop-title-1": [
          "2rem",
          {
            lineHeight: "1.25",
            letterSpacing: "0em",
          },
        ],
        "desktop-title-2": [
          "1.75rem",
          {
            lineHeight: "1.2857",
            letterSpacing: "0em",
          },
        ],
        "desktop-title-3": [
          "1.625rem",
          {
            lineHeight: "1.1538",
            letterSpacing: "0em",
          },
        ],
        "desktop-title-4": [
          "1.5rem",
          {
            lineHeight: "1.3333",
            letterSpacing: "0em",
          },
        ],
        "desktop-title-5": [
          "1.25rem",
          {
            lineHeight: "1.6",
            letterSpacing: "0em",
          },
        ],
        "desktop-title-6": [
          "1.125rem",
          {
            lineHeight: "1.7778",
            letterSpacing: "0em",
          },
        ],
        "mobile-headline-1": [
          "2rem",
          {
            lineHeight: "1.1875",
            letterSpacing: "0em",
          },
        ],
        "mobile-title-1": [
          "1.75rem",
          {
            lineHeight: "1.2857",
            letterSpacing: "0em",
          },
        ],
        "mobile-title-2": [
          "1.5rem",
          {
            lineHeight: "1.3333",
            letterSpacing: "0em",
          },
        ],
        "mobile-title-3": [
          "1.125rem",
          {
            lineHeight: "1.4444",
            letterSpacing: "0em",
          },
        ],
        "mobile-title-4": [
          "1rem",
          {
            lineHeight: "1.5",
            letterSpacing: "0em",
          },
        ],
        "mobile-title-5": [
          "0.875rem",
          {
            lineHeight: "1.2857",
            letterSpacing: "0em",
          },
        ],
        "mobile-title-6": [
          "0.875rem",
          {
            lineHeight: "1.2857",
            letterSpacing: "0em",
          },
        ],
        "desktop-body-1": [
          "1.125rem",
          {
            lineHeight: "1.4444",
            letterSpacing: "0em",
          },
        ],
        "desktop-body-2": [
          "1rem",
          {
            lineHeight: "1.5",
            letterSpacing: "0em",
          },
        ],
        "desktop-body-3": [
          "0.875rem",
          {
            lineHeight: "1.4286",
            letterSpacing: "0em",
          },
        ],
        "desktop-body-4": [
          "0.75rem",
          {
            lineHeight: "1.6667",
            letterSpacing: "0em",
          },
        ],
        "mobile-body-1": [
          "1.125rem",
          {
            lineHeight: "1.5556",
            letterSpacing: "-0.0018em",
          },
        ],
        "mobile-body-2": [
          "1rem",
          {
            lineHeight: "1.75",
            letterSpacing: "-0.0016em",
          },
        ],
        "mobile-body-3": [
          "0.9375rem",
          {
            lineHeight: "1.7333",
            letterSpacing: "-0.0015em",
          },
        ],
        "mobile-body-4": [
          "0.875rem",
          {
            lineHeight: "1.7143",
            letterSpacing: "-0.0014em",
          },
        ],
        "label-1": [
          "1.125rem",
          {
            lineHeight: "1.2222",
            letterSpacing: "0em",
          },
        ],
        "label-2": [
          "1rem",
          {
            lineHeight: "1.5",
            letterSpacing: "0em",
          },
        ],
        "label-3": [
          "0.875rem",
          {
            lineHeight: "1.4286",
            letterSpacing: "0em",
          },
        ],
        "label-4": [
          "0.75rem",
          {
            lineHeight: "1.3333",
            letterSpacing: "0em",
          },
        ],
        "label-5": [
          "0.625rem",
          {
            lineHeight: "1.4",
            letterSpacing: "0em",
          },
        ],
        "label-6": [
          "0.4375rem",
          {
            lineHeight: "1.4286",
            letterSpacing: "0em",
          },
        ],
        ".speciallabels-speciallabel-1": [
          "4rem",
          {
            lineHeight: "1.0312",
            letterSpacing: "-0.0192em",
          },
        ],
        ".speciallabels-speciallabel-2": [
          "3.25rem",
          {
            lineHeight: "1.0769",
            letterSpacing: "-0.0156em",
          },
        ],
        ".speciallabels-speciallabel-3": [
          "2.5rem",
          {
            lineHeight: "1.2",
            letterSpacing: "-0.012em",
          },
        ],
        ".speciallabels-speciallabel-4": [
          "1.25rem",
          {
            lineHeight: "1.3",
            letterSpacing: "-0.006em",
          },
        ],
      },
      fontFamily: {
        secondary: ["var(--font-secondary)", ...fontFamily.sans],
        syne: ["var(--font-syne)", ...fontFamily.sans],
        primary: ["var(--font-primary)", ...fontFamily.sans],
        inter: ["var(--font-inter)", ...fontFamily.sans],
      },
      fontWeight: {
        "headings-default": "500",
        "headings-prominent-1": "600",
        "headings-prominent-2": "600",
        "body-default": "400",
        "body-prominent-1": "500",
        "body-prominent-2": "600",
        "labels-default": "300",
        "labels-prominent-1": "400",
        "labels-prominent-2": "500",
        "special-labels-default": "400",
        "special-labels-prominent-1": "500",
        "special-labels-prominent-2": "600",
        "special-labels-prominent-3": "700",
      },
      animation: {
        "salary-flash-up": "salary-flash-up 1s ease-in-out",
        "salary-flash-down": "salary-flash-down 1s ease-in-out",
      },
    },
  },
};

export default config;
